// @ts-nocheck
import { useState, useEffect, useCallback, useMemo } from "react";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";

import DashboardLayout from "lib/LayoutContainers/DashboardLayout";
import DataTable from "lib/Tables/DataTable";

import Loader from "components/Loader/Content";
import TableFilter from "pages/applications/components/TableFilter";
import DataPipeline from "pages/applications/components/DataPipeline";
import { useStoreActions, useStoreState } from "easy-peasy";
import withDommer from "pages/withDommer";
import { useNavigate } from "react-router-dom";
import { Popover, MenuItem } from "@mui/material";
import { post, get } from "requests";
import { formatServerDate } from "utils/CalanderUtils";
import FullPageLoader from "components/Loader/FullPage";
import StorageUtils from "utils/StorageUtils";
import ExcelExportUtil from "utils/DataExportUtils";
import {
  Select,
  FormControl,
  InputLabel,
  Input,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import { ExpandMore } from "@mui/icons-material";
import moment from "moment";
import {
  JUNIOR_UNDERWRITER,
  ROLE_NAME,
  SCRUBBER,
  SENIOR_UNDERWRITER,
  SUBMISSION,
} from "constants/Constants";

function List(): JSX.Element {
  const navigate = useNavigate();
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [view, setView] = useState("");
  const [pipelineColumns, setPipelineColumns] = useState<any>([]);
  const [jrUnderwriterVisibleSteps, setJrUnderwriterVisibleSteps] = useState([]);
  const [srUnderwriterVisibleSteps, setSrUnderwriterVisibleSteps] = useState([]);

  const [rangeOpen, setRangeOpen] = useState(false);

  const [isCreating, setIsCreating] = useState(false);

  const filters = useStoreState((state) => state.applications.filters);
  const dateFilters = useStoreState((state) => state.applications.dateFilters);
  const range = useStoreState((state) => state.applications.range);
  const searchQuery = useStoreState((state) => state.applications.searchQuery);

  const setFilters = useStoreActions((actions) => actions.applications.setFilters);
  const setDateFilters = useStoreActions((actions) => actions.applications.setDateFilters);
  const setRange = useStoreActions((actions) => actions.applications.setRange);
  const setSearchQuery = useStoreActions((actions) => actions.applications.setSearchQuery);

  const fetchApplications = useStoreActions((actions) => actions.applications.fetchApplications);
  const columns = useStoreState((state) => state.applications.columns);
  const allApplications = useStoreState((state) => state.applications.applications);
  const [stateLoading, setStateLoading] = useState<any>(false);
  const roleName = localStorage.getItem(ROLE_NAME);

  const [anchorEl, setAnchorEl] = useState(null);

  const applications = useMemo(() => {
    return allApplications.filter((application) => {
      const status = application.status;
      const subStatus = application.subStatus;
      const appDate = new Date(application.applicationDate);
      const { fromDate, toDate } = dateFilters;
      const isWithinDateRange =
        (!fromDate || appDate >= new Date(fromDate)) && (!toDate || appDate <= new Date(toDate));

      const searchText = searchQuery?.toLowerCase() || "";

      const matchesSearch =
        !searchText ||
        [
          application.merchantName?.toLowerCase(),
          application.broker?.toLowerCase(),
          application.applicationNo?.toString()?.toLowerCase(),
          application.representativeName?.toLowerCase(),
          application.funder?.toLowerCase(),
        ].some((field) => field?.includes(searchText));

      if (roleName?.toLowerCase() === JUNIOR_UNDERWRITER) {
        const substatusName = application?.subStatusName;
        if (
          !substatusName ||
          !Array.isArray(jrUnderwriterVisibleSteps) ||
          !jrUnderwriterVisibleSteps.includes(substatusName)
        ) {
          return false;
        }
      }

      if (roleName?.toLowerCase() === SENIOR_UNDERWRITER) {
        const substatusName = application?.subStatusName;
        if (
          !substatusName ||
          !Array.isArray(srUnderwriterVisibleSteps) ||
          !srUnderwriterVisibleSteps.includes(substatusName)
        ) {
          return false;
        }
      }
      return (
        isWithinDateRange &&
        matchesSearch &&
        ((filters.Pending &&
          (status === "Pending" ||
            (status === "Closed" && (subStatus !== "Approved") & (subStatus !== "Declined")))) ||
          (filters.Declined && application.subStatus === "Declined") ||
          (filters.Approved && status === "Closed" && subStatus === "Approved"))
      );
    });
  }, [
    allApplications,
    filters,
    dateFilters,
    jrUnderwriterVisibleSteps,
    srUnderwriterVisibleSteps,
    searchQuery,
  ]);

  const loading = useStoreState((state) => state.applications.loading);

  const handleApplicationSelect = useCallback((row) => {
    const id = row?.original?.id;
    setSelectedApplication((prev) => (prev === id ? null : id));
  }, []);

  const handleViewChange = useCallback((viewType) => {
    setView(viewType);
  }, []);

  const handleFilterChange = useCallback(
    (filter) => {
      const updated = {
        ...filters,
        [filter]: !filters[filter],
      };
      setFilters(updated);
    },
    [filters, setFilters]
  );

  const handleExport = () => {
    if (!applications.length) return;

    const exportColumns = columns.map(({ Header, accessor }) => ({
      label: Header,
      accessor: accessor,
      Header: Header,
      key: accessor,
    }));

    const exportRows = applications.map((app) => {
      const row = {};
      exportColumns.forEach(({ key }) => {
        row[key] = app[key] !== undefined && app[key] !== null ? app[key] : "";
      });
      return row;
    });

    const now = new Date();
    const formattedDate = now.toISOString().replace(/[:.-]/g, "");
    const fileName = `applications_export_${formattedDate}.xlsx`;

    ExcelExportUtil.exportToExcel(exportColumns, exportRows, "Applications", fileName);
  };

  const handleCustomDateFilters = (e) => {
    const selectedRange = e.target.value;
    setRange(selectedRange);

    let fromDate = null;
    let toDate = moment();

    switch (selectedRange) {
      case "Today":
        fromDate = moment().startOf("day");
        toDate = moment().endOf("day");
        break;
      case "This Week":
        fromDate = moment().startOf("week");
        break;
      case "This Month":
        fromDate = moment().startOf("month");
        break;
      case "This Year":
        fromDate = moment().startOf("year");
        break;
      case "All Time":
      default:
        fromDate = null;
        toDate = null;
        break;
    }

    setDateFilters({
      fromDate: fromDate ? fromDate.toDate() : null,
      toDate: toDate ? toDate.toDate() : null,
    });
  };

  const handleResetFilters = () => {
    setRange("All Time");
    setRangeOpen(false);
    handleCustomDateFilters({ target: { value: "All Time" } });
    setSearchQuery("");
    setFilters({
      Pending: true,
      Declined: true,
      Approved: true,
    });
  };

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        let response = await get("/steps/getLatestStep");
        console.log("Fetched columns:", response);
        response.push("DECLINED");
        setPipelineColumns(response);
      } catch (error) {
        console.error("Error fetching columns:", error);
      }
    };

    const fetchThreshold = async () => {
      try {
        const response = await get("/steps/thresholds");
        const threshold = response?.jrUnderWriterStepNames;
        const srThresholds = response?.srUnderWriterStepNames;

        if (threshold) {
          setJrUnderwriterVisibleSteps(threshold);
        } else {
          setJrUnderwriterVisibleSteps([]);
        }

        if (srThresholds) {
          setSrUnderwriterVisibleSteps(srThresholds);
        } else {
          setSrUnderwriterVisibleSteps([]);
        }
      } catch (error) {
        console.error("Error fetching  Underwriter thresholds:", error);
        setJrUnderwriterVisibleSteps([]);
        setSrUnderwriterVisibleSteps([]);
      }
    };

    const loadData = async () => {
      try {
        setStateLoading(true);
        const isListView = StorageUtils.isFeatureEnabled("defaultListView");

        if (isListView) {
          setView("list");
        } else {
          setView("pipeline");
        }
        await fetchColumns();
        await fetchApplications();
        await fetchThreshold();
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setStateLoading(false);
      }
    };

    loadData();
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);

  const handleNewApplication = (type: any) => {
    if (isCreating) return;
    setIsCreating(true);

    let preApproved = type !== "Application";
    let tenantId = Number(localStorage.getItem("tenantId"));
    let userId = Number(localStorage.getItem("logInUserId"));

    post("dealApplication/create", {
      tenantId: tenantId,
      userId: userId,
      applicationDate: formatServerDate(new Date()),
      preApproved: preApproved,
    })
      .then((responseData) => {
        const id = responseData?.id;
        if (id) {
          navigate(`/deal_applications/${id}`);
        }
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const handleDateChange = (key, value) => {
    setDateFilters({
      ...dateFilters,
      [key]: Array.isArray(value) && value.length === 0 ? null : value,
    });
  };

  const renderContent = () => {
    const filterComponent = (
      <TableFilter
        view={view}
        filters={filters}
        onViewChange={handleViewChange}
        onFilterChange={handleFilterChange}
        dateFilters={dateFilters}
        handleDateChange={(key, val) => handleDateChange(key, val)}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />
    );

    if (view === "pipeline") {
      return (
        <MDBox paddingBottom={2}>
          <DataPipeline
            columns={pipelineColumns}
            applications={applications}
            filterComponent={filterComponent}
          />
        </MDBox>
      );
    }
    return (
      <MDBox paddingBottom={2}>
        <DataTable
          table={{ columns, rows: applications }}
          entriesPerPage={true}
          canSearch={false}
          selectedItem={selectedApplication}
          onSelectItem={handleApplicationSelect}
          hasFilterComponent={true}
          onRowClick={(row: any) => {
            if (row?.original?.id) {
              navigate(`/deal_applications/${row?.original?.id}`);
            }
          }}
          filterComponent={filterComponent}
        />
      </MDBox>
    );
  };

  return (
    <DashboardLayout>
      <MDBox mb={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            display="flex"
            justifyContent="flex-start"
            alignItems="flex-start"
            mb={2}
            zIndex={5000}
          >
            <MDButton variant="gradient" color="primary" onClick={handleClick}>
              New Application
            </MDButton>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              sx={{
                marginTop: 1,
                zIndex: 9999,
              }}
            >
              <MDBox zIndex={10000} bgColor="white">
                <MenuItem onClick={() => handleNewApplication("Application")}>Application</MenuItem>

                {roleName !== SCRUBBER && roleName !== SUBMISSION && (
                  <MenuItem onClick={() => handleNewApplication("Pre-Approved Application")}>
                    Pre-Approved Application
                  </MenuItem>
                )}
              </MDBox>
            </Popover>
            {(loading || stateLoading) && <FullPageLoader />}

            {selectedApplication && (
              <MDButton
                variant="gradient"
                color="dark"
                className={"ml-10"}
                onClick={(event) => {
                  if (selectedApplication) {
                    navigate(`/deal_applications/${selectedApplication}`);
                  }
                }}
              >
                Edit
              </MDButton>
            )}
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end" alignItems="flex-start" mb={2}>
            <Tooltip title="Reset All Filters" arrow>
              <IconButton
                size="medium"
                onClick={handleResetFilters}
                sx={{
                  marginLeft: 1,
                  marginRight: 2,
                  height: "40px",
                  width: "40px",
                  border: "1px solid #ccc",
                  backgroundColor: "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              >
                <Icon sx={{ fontSize: 28 }}>filter_alt_off</Icon>
              </IconButton>
            </Tooltip>

            <Select
              color="primary"
              value={range}
              onChange={(e) => {
                const selectedRange = e.target.value;
                setRange(selectedRange);
                handleCustomDateFilters(e);
              }}
              open={rangeOpen}
              onOpen={() => setRangeOpen(true)}
              onClose={() => setRangeOpen(false)}
              renderValue={(value) => (
                <MDTypography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: "15px",
                    color: "#1A73E8",
                    ml: 1,
                  }}
                >
                  {value}
                </MDTypography>
              )}
              sx={{
                height: "38px",
                display: "flex",
                alignItems: "center",
                minWidth: 120,
                paddingLeft: 2,
                border: "2px solid",
                borderColor: "#1A73E8",
                borderRadius: "10px",
                "&.Mui-focused": {
                  border: "none",
                },
              }}
              IconComponent={() => (
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => setRangeOpen((prev) => !prev)}
                  sx={{ padding: 0, position: "absolute", left: 10 }}
                >
                  <Icon fontSize="small">query_builder</Icon>
                </IconButton>
              )}
              endAdornment={
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => setRangeOpen((prev) => !prev)}
                  sx={{ padding: 0, margin: 0 }}
                >
                  <ExpandMore />
                </IconButton>
              }
            >
              {["All Time", "Today", "This Week", "This Month", "This Year"].map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    py: 1,
                    "&.Mui-selected": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>

            {roleName && roleName?.toLowerCase() !== JUNIOR_UNDERWRITER && (
              <MDButton
                variant="outlined"
                color={"dark"}
                size={"small"}
                className={"ml-10"}
                style={{ height: "36px", marginRight: "10px" }}
                onClick={handleExport}
              >
                <Icon fontSize={"large"}>add</Icon>&nbsp; EXPORT
              </MDButton>
            )}
          </MDBox>
        </MDBox>
        <Card style={{ marginTop: "-10px", padding: "20px" }}>{renderContent()}</Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default withDommer(List);
